import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Firestore,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getQuizRef } from '@/data/quizzes';

export type QnaSort = 'top' | 'newest' | 'unanswered';

export type QnaItem = {
  id: string;
  question: string;
  votes: number;
  answered: boolean;
  highlighted?: boolean;
  createdAt?: any;
  authorUid?: string;
};

function qnaCol(firestore: Firestore, quizId: string) {
  return collection(getQuizRef(firestore, quizId), 'qna');
}

function qnaVotesCol(firestore: Firestore, quizId: string) {
  return collection(getQuizRef(firestore, quizId), 'qnaVotes');
}

export async function createQna(quizId: string, authorUid: string, question: string) {
  const ref = doc(qnaCol(db, quizId));
  await setDoc(ref, {
    question: question.trim(),
    votes: 0,
    answered: false,
    highlighted: false,
    authorUid,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function listQna(quizId: string, sort: QnaSort, max: number = 50): Promise<QnaItem[]> {
  const base = qnaCol(db, quizId);

  // Keep it simple/fast: newest uses createdAt, others sort locally after fetch.
  // Firestore ordering on votes would require an index and ignores secondary sorts.
  const q = query(base, orderBy('createdAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as QnaItem[];

  if (sort === 'newest') return items;

  if (sort === 'unanswered') {
    return items
      .filter(i => !i.answered)
      .sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0));
  }

  // top
  return items.filter(i => !(i as any).deleted).sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0));
}

export async function voteQna(quizId: string, qnaId: string, voterUid: string, delta: 1 | -1 = 1) {
  const qnaRef = doc(qnaCol(db, quizId), qnaId);
  const voteKey = `${voterUid}_${qnaId}`;
  const voteRef = doc(qnaVotesCol(db, quizId), voteKey);

  await runTransaction(db, async (tx) => {
    const voteSnap = await tx.get(voteRef);
    if (voteSnap.exists()) {
      // already voted (MVP: single vote only)
      return;
    }
    const qnaSnap = await tx.get(qnaRef);
    if (!qnaSnap.exists()) return;
    const prevVotes = (qnaSnap.data() as any).votes ?? 0;

    tx.set(voteRef, { voterUid, qnaId, createdAt: serverTimestamp() });
    tx.update(qnaRef, { votes: prevVotes + delta });
  });
}

export async function markQnaAnswered(quizId: string, qnaId: string, answered: boolean) {
  await updateDoc(doc(qnaCol(db, quizId), qnaId), { answered });
}

export async function toggleQnaHighlight(quizId: string, qnaId: string) {
  const ref = doc(qnaCol(db, quizId), qnaId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const cur = (snap.data() as any).highlighted ?? false;
    tx.update(ref, { highlighted: !cur });
  });
}

export async function deleteQna(quizId: string, qnaId: string) {
  await deleteDoc(doc(qnaCol(db, quizId), qnaId));
}
